import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ApiIcon from '@mui/icons-material/Api';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useTranslation } from 'react-i18next';

const AmazonStorePipelineAnimation = () => {
    const { t } = useTranslation();
    // Total duration is 10 seconds to allow for the exact pacing requested
    const transitionConfig = { duration: 10, repeat: Infinity, ease: "linear" };

    return (
        <Box sx={{ 
            width: '100%', 
            height: '100%', 
            minHeight: 280, 
            position: 'relative', 
            backgroundColor: 'rgba(0,0,0,0.2)', 
            borderRadius: 2, 
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden' 
        }}>
            {/* SVG Layer for Connecting Lines & Moving Data Packets */}
            <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}>
                {/* Background dashed pathways */}
                <line x1="15%" y1="45%" x2="50%" y2="45%" stroke="rgba(255,255,255,0.15)" strokeWidth="2" strokeDasharray="4,4" />
                <line x1="50%" y1="45%" x2="85%" y2="45%" stroke="rgba(255,255,255,0.15)" strokeWidth="2" strokeDasharray="4,4" />

                {/* Dot 1: App -> Vetra AIAPI (Image Upload) */}
                <motion.circle
                    cy="45%"
                    r="5"
                    fill="#50728C"
                    animate={{
                        cx: ['15%', '15%', '15%', '50%', '50%', '50%'],
                        opacity: [0, 0, 1, 1, 0, 0],
                        filter: ['none', 'none', 'drop-shadow(0 0 8px #50728C)', 'drop-shadow(0 0 8px #50728C)', 'none', 'none']
                    }}
                    // 0s: hide, 1.9s: prepare, 2.0s: show & start moving, 3.5s: arrive, 3.6s: hide, 10s: loop
                    transition={{ ...transitionConfig, times: [0, 0.09, 0.1, 0.25, 0.26, 1] }}
                />

                {/* Dot 2: Vetra AI API -> SIU (Fraud Alert) */}
                <motion.circle
                    cy="45%"
                    r="5"
                    fill="#f44336"
                    animate={{
                        cx: ['50%', '50%', '50%', '85%', '85%', '85%'],
                        opacity: [0, 0, 1, 1, 0, 0],
                        filter: ['none', 'none', 'drop-shadow(0 0 8px #f44336)', 'drop-shadow(0 0 8px #f44336)', 'none', 'none']
                    }}
                    // 0s: hide, 5.4s: prepare, 5.5s: show & start moving, 7.0s: arrive, 7.1s: hide, 10s: loop
                    transition={{ ...transitionConfig, times: [0, 0.59, 0.6, 0.75, 0.76, 1] }}
                />
            </svg>

            {/* Foreground UI Layer for Nodes */}
            <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2 }}>
                
                {/* Node 1: Mobile App */}
                <Box sx={{ position: 'absolute', left: '15%', top: '45%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <motion.div
                        animate={{ scale: [1, 1, 1.1, 1, 1, 1] }}
                        // Pulse at 1s
                        transition={{ ...transitionConfig, times: [0, 0.04, 0.05, 0.15, 0.95, 1] }}
                        style={{ 
                            backgroundColor: '#171F29', 
                            border: '2px solid rgba(255,255,255,0.3)', 
                            borderRadius: '50%', 
                            width: 56,           // Forces exact match
                            height: 56,          // Forces exact match
                            boxSizing: 'border-box',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <PhoneAndroidIcon sx={{ color: 'white', fontSize: 32 }} />
                    </motion.div>
                    
                    <Box sx={{ position: 'absolute', top: '100%', mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: 'max-content' }}>
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>{t('industryApplicationsAmazon.animations.amazonStorePipeline.node1Title')}</Typography>
                        
                        <motion.div 
                            animate={{ opacity: [0, 0, 1, 1, 0, 0] }} 
                            // Appears at 1.0s, stays until 2.0s (when dot leaves), fades out fully by 2.5s
                            transition={{ ...transitionConfig, times: [0, 0.04, 0.05, 0.15, 0.2, 1] }}
                        >
                            <Typography variant="caption" sx={{ color: '#50728C', fontSize: '0.7rem' }}>{t('industryApplicationsAmazon.animations.amazonStorePipeline.node1Action')}</Typography>
                        </motion.div>
                    </Box>
                </Box>

                {/* Node 2: Vetra AI API Server */}
                <Box sx={{ position: 'absolute', left: '50%', top: '45%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <motion.div
                        animate={{ 
                            borderColor: [
                                'rgba(255,255,255,0.3)', // 0: Idle
                                'rgba(255,255,255,0.3)', // 0.33: Wait
                                'rgba(80,114,140,0.8)',  // 0.34: Box turns Blue
                                'rgba(80,114,140,0.8)',  // 0.52: Box stays Blue
                                'rgba(244,67,54,0.8)',   // 0.53: Box turns Red
                                'rgba(244,67,54,0.8)',   // 0.55: Dot leaves (Still Red)
                                'rgba(255,255,255,0.3)', // 0.60: Fast fade out
                                'rgba(255,255,255,0.3)'  // 1: Idle
                            ],
                            boxShadow: [
                                'none', 
                                'none', 
                                '0 0 20px rgba(80,114,140,0.4)', 
                                '0 0 20px rgba(80,114,140,0.4)', 
                                '0 0 25px rgba(244,67,54,0.5)', 
                                '0 0 25px rgba(244,67,54,0.5)', 
                                'none', 
                                'none'
                            ]
                        }}
                        transition={{ ...transitionConfig, times: [0, 0.23, 0.24, 0.43, 0.44, 0.60, 0.65, 1] }}
                        style={{ 
                            backgroundColor: '#171F29', 
                            border: '2px solid rgba(255,255,255,0.3)', 
                            borderRadius: '12px', 
                            width: 56,           // Forces exact match
                            height: 56,          // Forces exact match
                            boxSizing: 'border-box',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <ApiIcon sx={{ color: 'white', fontSize: 32 }} />
                    </motion.div>
                    
                    <Box sx={{ position: 'absolute', top: '100%', mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: 'max-content' }}>
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>{t('industryApplicationsAmazon.animations.amazonStorePipeline.node2Title')}</Typography>
                        
                        <Box sx={{ position: 'relative', height: 20, width: '100%', mt: 0.2, display: 'flex', justifyContent: 'center' }}>
                            {/* Blue Text: Analyzing... */}
                            <motion.div 
                                style={{ position: 'absolute', textAlign: 'center' }} 
                                animate={{ opacity: [0, 0, 1, 1, 0, 0] }} 
                                // Fades in right AFTER box turns blue, fades out right BEFORE box turns red
                                transition={{ ...transitionConfig, times: [0, 0.24, 0.25, 0.43, 0.44, 1] }}
                            >
                                <Typography variant="caption" sx={{ color: '#50728C', fontSize: '0.7rem' }}>{t('industryApplicationsAmazon.animations.amazonStorePipeline.node2Action')}</Typography>
                            </motion.div>

                            {/* Red Text: Likely Synthetic */}
                            <motion.div 
                                style={{ position: 'absolute', textAlign: 'center' }} 
                                animate={{ opacity: [0, 0, 1, 1, 0, 0] }} 
                                // Fades in right AFTER box turns red, fades out starting right as dot leaves
                                transition={{ ...transitionConfig, times: [0, 0.44, 0.45, 0.60, 0.65, 1] }}
                            >
                                <Typography variant="caption" sx={{ color: '#f44336', fontSize: '0.75rem', fontWeight: 'bold' }}>{t('industryApplicationsAmazon.animations.amazonStorePipeline.node2Result')}</Typography>
                            </motion.div>
                        </Box>
                    </Box>
                </Box>

                {/* Node 3: SIU Desk Dashboard */}
                <Box sx={{ position: 'absolute', left: '85%', top: '45%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <motion.div
                        animate={{ 
                            scale: [1, 1, 1.1, 1.1, 1],
                            borderColor: ['rgba(255,255,255,0.3)', 'rgba(255,255,255,0.3)', 'rgba(244,67,54,0.8)', 'rgba(244,67,54,0.8)', 'rgba(255,255,255,0.3)'],
                            boxShadow: ['none', 'none', '0 0 25px rgba(244,67,54,0.6)', '0 0 25px rgba(244,67,54,0.6)', 'none'],
                            color: ['#fff', '#fff', '#f44336', '#f44336', '#fff']
                        }}
                        // 0-6.9s: Idle, 7.0s-9.5s: Flagged (Red), 9.5s-10s: Reset
                        transition={{ ...transitionConfig, times: [0, 0.69, 0.7, 0.95, 1] }}
                        style={{ 
                            backgroundColor: '#171F29', 
                            border: '2px solid rgba(255,255,255,0.3)', 
                            borderRadius: '50%', 
                            width: 56,           // Forces exact match
                            height: 56,          // Forces exact match
                            boxSizing: 'border-box',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <AdminPanelSettingsIcon sx={{ fontSize: 32 }} color="inherit" />
                    </motion.div>
                    
                    <Box sx={{ position: 'absolute', top: '100%', mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: 'max-content' }}>
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>{t('industryApplicationsAmazon.animations.amazonStorePipeline.node3Title')}</Typography>
                        
                        <motion.div 
                            animate={{ opacity: [0, 0, 1, 1, 0, 0] }} 
                            // Appears at 7.0s, stays until 9.5s
                            transition={{ ...transitionConfig, times: [0, 0.69, 0.7, 0.95, 0.96, 1] }}
                        >
                            <Typography variant="caption" sx={{ color: '#f44336', fontSize: '0.75rem', fontWeight: 'bold' }}>{t('industryApplicationsAmazon.animations.amazonStorePipeline.node3Result')}</Typography>
                        </motion.div>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default AmazonStorePipelineAnimation;